/**
 * EffectivenessChart — three-window roll-up of skill effectiveness.
 *
 * Renders the all-time / 30-day / 90-day composite_score as a compact
 * stat row + a slim SVG bar visualising the ratio so the founder can
 * compare windows at a glance. Monochrome chrome (ADR-057); the green/
 * yellow/red tone is reserved for the score itself per UI guidelines.
 */
import type { EffectivenessWindow, SkillEffectivenessDetailed } from '@chaos/types/learning';

import { cn } from '../../lib/utils';
import { effectivenessTone, fmtCount, fmtScore, TONE_TEXT_CLASS } from './formatters';

interface EffectivenessChartProps {
  detail: SkillEffectivenessDetailed;
  className?: string;
}

type WindowKey = 'all_time' | 'window_30d' | 'window_90d';

const WINDOWS: ReadonlyArray<{ key: WindowKey; label: string }> = [
  { key: 'all_time', label: 'All time' },
  { key: 'window_30d', label: '30 days' },
  { key: 'window_90d', label: '90 days' },
];

export function EffectivenessChart({ detail, className }: EffectivenessChartProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded border border-border bg-background p-4',
        className,
      )}
    >
      <header className="flex items-baseline justify-between">
        <h3 className="text-[13px] font-semibold text-foreground">Effectiveness</h3>
        <span className="text-[11px] text-muted-foreground">
          green ≥ 80% · yellow ≥ 50% · red &lt; 50%
        </span>
      </header>
      <div className="grid grid-cols-3 gap-3">
        {WINDOWS.map(({ key, label }) => (
          <WindowStat key={key} label={label} window={detail[key]} />
        ))}
      </div>
    </div>
  );
}

function WindowStat({ label, window }: { label: string; window: EffectivenessWindow }) {
  const tone = effectivenessTone(window.composite_score);
  const pct = Math.max(0, Math.min(1, window.composite_score ?? 0));
  const widthClass = pct === 0 ? '0%' : `${pct * 100}%`;
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className={cn('font-mono text-2xl font-semibold tabular-nums', TONE_TEXT_CLASS[tone])}>
        {fmtScore(window.composite_score)}
      </span>
      <div
        role="img"
        aria-label={`${label} effectiveness ${fmtScore(window.composite_score)}`}
        className="relative h-1 w-full overflow-hidden rounded-full bg-muted"
      >
        <span
          aria-hidden
          className={cn(
            'absolute inset-y-0 left-0 block transition-[width] duration-300',
            tone === 'success' && 'bg-success',
            tone === 'warning' && 'bg-warning',
            tone === 'error' && 'bg-destructive',
            tone === 'muted' && 'bg-muted-foreground/40',
          )}
          style={{ width: widthClass }}
        />
      </div>
      <dl className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
        <span>
          <dt className="inline">Attempts:</dt>{' '}
          <dd className="inline font-medium text-foreground">{fmtCount(window.total_attempts)}</dd>
        </span>
        <span>
          <dt className="inline">Successes:</dt>{' '}
          <dd className="inline font-medium text-foreground">{fmtCount(window.successes)}</dd>
        </span>
        <span>
          <dt className="inline">1st-try:</dt>{' '}
          <dd className="inline font-medium text-foreground">
            {fmtCount(window.first_try_successes)}
          </dd>
        </span>
      </dl>
    </div>
  );
}
